import { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faUser, faBriefcase } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Modal from "../../components/modal/modal";
import { UserContext } from "../../components/usercontext/user_context";

const Login = () => {
  const { login } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // 'worker' — соискатель, 'company' — работодатель
  const [role, setRole] = useState("worker"); 
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [view, setView] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const verified = searchParams.get("verified");
    const verifyError = searchParams.get("verify_error");
    if (verified === "1") {
      setModalMessage({
        title: t("success"),
        text: t("email_verified_login"),
        success: true,
      });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("verified");
          return next;
        },
        { replace: true },
      );
    } else if (verifyError) {
      setModalMessage({
        title: t("error"),
        text: t("verify_error_generic"),
        success: false,
      });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("verify_error");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams.get("verified"), searchParams.get("verify_error"), setSearchParams, t]);

  const handleView = () => setView(!view);
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Динамически выбираем эндпоинт в зависимости от активной вкладки
    const endpoint = role === "company" ? "/api/employerlogin.php" : "/api/workerlogin.php";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        const userData = data.user;
        // Сохраняем чистые данные в localStorage и обновляем контекст
        localStorage.setItem("userId", userData.id);
        localStorage.setItem("user", JSON.stringify(userData));
        login(userData); 

        // Обработка отложенного лайка (только для работников)
        if (role === "worker") {
          const pendingLike = localStorage.getItem("pending_like");
          if (pendingLike) {
            try {
              await fetch("/api/toggle_like.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  user_id: userData.id,
                  vacancy_id: parseInt(pendingLike),
                }),
              });
              localStorage.removeItem("pending_like");
            } catch (e) {
              console.error("Ошибка при добавлении отложенного лайка:", e);
            }
          }
        }

        // Перенаправление на нужный профиль
        if (role === "company" || userData.role === "company") {
          navigate("/employerprofile");
        } else {
          navigate("/workerprofile");
        }

      } else {
        const isUnverified = data.code === "email_not_verified";
        setModalMessage({
          title: t("error"),
          text: isUnverified ? t("email_not_verified_login") : data.message || t("errormail"),
          success: false,
        });
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setModalMessage({
        title: t("error"),
        text: t("errorserver"),
        success: false,
      });
    }
  };

  return (
    <>
      {modalMessage && (
        <Modal
          title={modalMessage.title}
          text={modalMessage.text}
          ok={t("ok")}
          success={modalMessage.success}
          onClose={() => setModalMessage(null)}
        />
      )}

      <div className="w-[500px] max-sm:w-[90%] h-auto m-auto opacity-0 animate-[showtitle_1s_forwards] flex flex-col rounded-2xl shadow-2xl shadow-black border-[2px] border-[#006674] bg-white overflow-hidden">
        
        {/* Переключатель ролей (Табы) */}
        <div className="flex w-full bg-gray-100 border-b-[2px] border-[#006674]">
          <button
            type="button"
            className={`flex-1 py-3 font-semibold text-lg max-sm:text-sm flex items-center justify-center gap-2 transition-all duration-200 ${
              role === "worker"
                ? "bg-[#006674] text-white"
                : "text-[#006674] hover:bg-gray-200"
            }`}
            onClick={() => setRole("worker")}
          >
            <FontAwesomeIcon icon={faUser} />
            {t("find_job")} {/* Добавь перевод или оставь текст */}
          </button>
          <button
            type="button"
            className={`flex-1 py-3 font-semibold text-lg max-sm:text-sm flex items-center justify-center gap-2 transition-all duration-200 ${
              role === "company"
                ? "bg-[#006674] text-white"
                : "text-[#006674] hover:bg-gray-200"
            }`}
            onClick={() => setRole("company")}
          >
            <FontAwesomeIcon icon={faBriefcase} />
            {t("im_employer")} {/* Добавь перевод или оставь текст */}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="w-full p-5 flex flex-col gap-3">
          <h3 className="font-bold text-[#006674] w-full text-center uppercase text-2xl max-sm:text-xl mb-2">
            {role === "worker" ? t("login2") : t("employer_login")}
          </h3>

          <div className="w-full flex flex-col gap-1">
            <label htmlFor="email" className="text-[#006674] max-sm:text-sm">
              {t("email")}
            </label>
            <input
              type="email"
              name="email"
              id="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full py-2 px-4 outline-none rounded-md border-[1px] border-gray-400 focus:border-[#006674] focus:shadow-xl transition-all duration-200"
            />

            <label htmlFor="password" className="text-[#006674] max-sm:text-sm mt-2">
              {t("password")}
            </label>
            <div className="flex w-full justify-between items-center gap-3">
              <input
                type={view ? "text" : "password"}
                name="password"
                id="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full py-2 px-4 outline-none rounded-md border-[1px] border-gray-400 focus:border-[#006674] focus:shadow-xl transition-all duration-200"
              />
              <button
                onClick={handleView}
                type="button"
                className="min-w-[40px] min-h-[40px] flex justify-center items-center rounded-full bg-[#006674] text-white"
              >
                <FontAwesomeIcon icon={view ? faEyeSlash : faEye} />
              </button>
            </div>

            <div className="w-full flex mt-3 justify-between items-center">
              <Link
                to="/resetpassword"
                className="text-[#006674] max-sm:text-[14px] cursor-pointer transition-all duration-200 hover:text-[#848484]"
              >
                {t("resetpasswordtext")}
              </Link>
            </div>

            <button
              type="submit"
              className="py-2 px-4 w-full capitalize mt-4 bg-[#006674] text-white rounded-[30px] font-semibold transition-all duration-200 hover:bg-[#848484]"
            >
              {t("login2")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Login;